// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { IconWrapper } from "@insite/mobius/Icon";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { buttonTransparent } from "@keller/XDL/PreStyle/button";
import { xBaseTheme } from "@keller/XDL/types";
import { color, ellipsis, styledScrollbar, xScreen } from "@keller/XDL/Utilities";
import { css } from "styled-components";

const MODAL_DESKTOP_PADDING = 25;
const MODAL_MOBILE_PADDING = 20;

export default {
    sizeVariants: {
        small: 350,
        medium: 908,
        large: 1200,
    },
    defaultProps: {
        mergeCss: true,
        cssOverrides: {
            modalBody: css`
                overflow: hidden;
                border: 1px solid ${color("common.border")};
                border-radius: 8px;
                background: ${color("common.white")};
                &&&& {
                    box-shadow: ${getProp("theme.shadows.4")};
                }
                overflow: unset;

                //Delete this media query for a full-screen modal on mobile
                ${xScreen("lte", "md")} {
                    max-width: 90%;
                    height: auto;
                    max-height: calc(100% - 35px);
                    margin: 15px;
                }
            `,
            headlineTypography: css`
                display: inline-block;
                width: 100%;
                letter-spacing: 0;
                line-height: 28px;
                margin-bottom: 0;
                word-wrap: break-word;
                white-space: pre-wrap;
                padding-right: 28px;
            `,
            titleButton: css`
                &&&& {
                    ${buttonTransparent}

                    width: 40px;
                    height: 40px;
                    position: absolute;
                    top: 20px;
                    right: 10px;
                    padding: 12px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1;
                    &:focus {
                        outline: none;
                    }

                    & > ${IconWrapper} {
                        position: unset;
                        top: 0;
                        color: ${color("common.ui16")};
                    }
                    &:hover:not(:disabled) {
                        background-color: ${color("common.ui12")};
                        & > ${IconWrapper} {
                            color: ${color("common.ui16", 0.6)};
                        }
                    }
                }
            `,
            modalTitle: css`
                &&& {
                    border: none;
                    padding: ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING / 2}px;
                    ${xScreen("lte", "xs")} {
                        padding-left: ${MODAL_MOBILE_PADDING}px;
                        padding-right: ${MODAL_MOBILE_PADDING}px;
                        ${TypographyStyle as any} {
                            font-size: 24px;
                        }
                    }
                    & > h4 {
                        padding-right: 0;
                        ${ellipsis(1)}
                    }
                }
            `,
            modalContent: css`
                &&& {
                    padding: ${MODAL_DESKTOP_PADDING / 2}px ${MODAL_DESKTOP_PADDING}px ${MODAL_DESKTOP_PADDING}px;
                }

                flex: 1;
                display: flex;
                flex-direction: column;

                ${styledScrollbar()}

                .modal-actions {
                    display: flex;
                    flex: 0 0 auto;
                    flex-wrap: wrap;
                    justify-content: flex-end;
                    width: 100%;
                    margin-top: 30px;

                    background: ${color("common.white")};

                    button {
                        margin: 0 0 10px 0;
                    }
                }

                ${xScreen("lte", "xs")} {
                    padding-left: ${MODAL_MOBILE_PADDING}px;
                    padding-right: ${MODAL_MOBILE_PADDING}px;

                    .modal-actions {
                        flex-direction: column;
                        justify-content: center;

                        button {
                            margin: 10px 0 0 0;
                            width: 100%;

                            &:first-child {
                                margin-top: 0;
                            }
                        }
                    }
                }
            `,
        },
        sizeVariant: "medium",
        headlineTypographyProps: {
            variant: "h4",
            forwardAs: "h3",
            size: 24,
            weight: 900,
        },
        closeButtonProps: {
            buttonType: "solid",
            sizeVariant: "small",
            shape: "pill",
            variant: "primary",
            color: "common.white",
            size: 42,
        },
        closeButtonIconProps: {
            size: 20,
            color: "common.white",
        },
    },
} as xBaseTheme["modal"];
